/**
 * Bulk action buttons when sent drops are selected.
 */

import type { FC, ReactNode } from 'react';
import { ClipboardPaste, Copy, Edit3, Layers, StopCircle, X } from 'lucide-react';

import { Button } from '@/components/ui';

export interface SentSelectionActionButtonsProps {
  selectedCount: number;
  selectedWithSentIdCount: number;
  isBusy: boolean;
  onApplyCopied?: () => void;
  hasCopiedValues?: boolean;
  onCopyAsDraft?: () => void;
  onSaveAsPreset?: () => void;
  /** Re-send / Re-schedule for killed drops — renders nothing without any. */
  resendSlot?: ReactNode;
  onKill?: () => void;
  onEdit?: () => void;
  onClearSelection: () => void;
}

export const SentSelectionActionButtons: FC<SentSelectionActionButtonsProps> = ({
  selectedCount,
  selectedWithSentIdCount,
  isBusy,
  onApplyCopied,
  hasCopiedValues,
  onCopyAsDraft,
  onSaveAsPreset,
  resendSlot,
  onKill,
  onEdit,
  onClearSelection,
}) => (
  <div className="flex items-center gap-2">
    {onApplyCopied && hasCopiedValues && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || selectedWithSentIdCount === 0}
        onClick={onApplyCopied}
        aria-label="Apply copied config to selected sent drops"
        title="Preview copied values then open editor"
      >
        <ClipboardPaste className="h-4 w-4" />
        Apply copied
      </Button>
    )}
    {onCopyAsDraft && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || selectedCount === 0}
        onClick={onCopyAsDraft}
        aria-label="Copy selected as drafts"
      >
        <Copy className="h-4 w-4" />
        Copy as draft ({selectedCount})
      </Button>
    )}
    {onSaveAsPreset && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || selectedCount === 0}
        onClick={onSaveAsPreset}
        aria-label="Save as preset"
      >
        <Layers className="h-4 w-4" />
        Save as preset ({selectedCount})
      </Button>
    )}
    {resendSlot}
    {onKill && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
        disabled={isBusy || selectedWithSentIdCount === 0}
        onClick={onKill}
        aria-label="Kill selected drops"
      >
        <StopCircle className="h-4 w-4" />
        Kill ({selectedWithSentIdCount})
      </Button>
    )}
    {onEdit && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || selectedWithSentIdCount === 0}
        onClick={onEdit}
      >
        <Edit3 className="h-4 w-4" />
        Edit
      </Button>
    )}
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-9 w-9 p-0"
      onClick={onClearSelection}
      disabled={isBusy}
      aria-label="Clear selection"
      title="Clear"
    >
      <X className="h-4 w-4" />
    </Button>
  </div>
);
